import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  Badge,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react'
import { FiEye } from 'react-icons/fi'
import { getNoticeList } from '../services/apiService'
import dayjs from 'dayjs'

interface NoticePost {
  id: number
  userId: number
  username: string
  title: string
  content: string
  viewCount: number
  createdAt: string
  updatedAt: string | null
}

const Notice: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const [notices, setNotices] = useState<NoticePost[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const fetchNotices = async () => {
    setLoading(true)
    try {
      const { data: res } = await getNoticeList(page, limit)
      if (res.statusCode === 200) {
        setNotices(res.data.notices)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('공지사항 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotices()
  }, [page])

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading
              as="h1"
              size={isMobile ? 'xl' : '2xl'}
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
              fontWeight="bold"
              mb={4}
            >
              {t('공지사항')}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {t('중요한 공지사항을 확인하세요')}
            </Text>
          </Box>

          {loading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" color="brand.500" />
            </Box>
          ) : notices.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500" fontSize="lg">
                {t('공지사항이 없습니다.')}
              </Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {notices.map((notice) => (
                <Card
                  key={notice.id}
                  cursor="pointer"
                  _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                  onClick={() => navigate(`/notice/${notice.id}`)}
                >
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={1} flex={1}>
                          <Heading size="md" noOfLines={2}>
                            {notice.title}
                          </Heading>
                          <Text fontSize="sm" color="gray.500" noOfLines={2}>
                            {notice.content}
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack justify="space-between" fontSize="sm" color="gray.500">
                        <HStack spacing={4}>
                          <Text>{notice.username}</Text>
                          <Text>•</Text>
                          <Text>{dayjs(notice.createdAt).format('YYYY.MM.DD')}</Text>
                        </HStack>
                        <HStack spacing={2}>
                          <FiEye />
                          <Text>{notice.viewCount}</Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <HStack justify="center" spacing={2} mt={4}>
              <Button
                size="sm"
                onClick={() => setPage(page - 1)}
                isDisabled={page === 1}
              >
                {t('이전')}
              </Button>
              <Text>
                {page} / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage(page + 1)}
                isDisabled={page === totalPages}
              >
                {t('다음')}
              </Button>
            </HStack>
          )}
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default Notice

